<template>
  <div class="modal-card">
    <header class="modal-card-head">
      <p class="modal-card-title">
        {{ title }}
      </p>
    </header>
    <section class="modal-card-body">
      <b-table
        :data="filteredItems"
        aria-next-label="Next page"
        aria-previous-label="Previous page"
        aria-page-label="Page"
        aria-current-label="Current page"
      >
        <b-table-column
          field="sort"
          :label="$t('attributes.sort')"
          :width="80"
          v-slot="props"
        >
          <div v-if="!props.row.editing && props.row.id">
            {{ props.row.data.sort }}
          </div>
          <div v-else>
            <b-field
              :type="!!props.row.errors['sort'] ? 'is-danger' : ''"
              class="ml-2"
            >
              <b-select v-model="props.row.modify.sort" name="sort">
                <option
                  v-for="option in sortOptions"
                  :key="option"
                  :value="option"
                >
                  {{ option }}
                </option>
              </b-select>
            </b-field>
          </div>
        </b-table-column>

        <b-table-column
          field="type"
          :label="$t('attributes.question_type')"
          :width="110"
          v-slot="props"
        >
          <div v-if="!props.row.editing && props.row.id">
            {{ typeName(props.row.data.type) }}
          </div>
          <div v-else>
            <b-field
              :type="!!props.row.errors['type'] ? 'is-danger' : ''"
              class="ml-2"
            >
              <b-select
                id="__question_type"
                v-model="props.row.modify.type"
                name="__question_type"
              >
                <option value="Option">{{ $t('attributes.option') }}</option>
                <option value="Text">{{ $t('attributes.text') }}</option>
              </b-select>
            </b-field>
          </div>
        </b-table-column>

        <b-table-column
          field="name"
          :label="$t('attributes.option_name')"
          v-slot="props"
        >
          <div v-if="!props.row.editing && props.row.id">
            {{ props.row.data.name }}
          </div>
          <div v-else>
            <b-field
              :type="!!props.row.errors['name'] ? 'is-danger' : ''"
              class="ml-2"
            >
              <b-input
                v-model="props.row.modify.name"
                :placeholder="$t('attributes.option')"
                @input="props.row.errors['name'] = null"
              ></b-input>
            </b-field>
          </div>
        </b-table-column>

        <b-table-column
          field="placeholder"
          :label="$t('attributes.placeholder')"
          v-slot="props"
        >
          <div v-if="!props.row.editing && props.row.id">
            {{ props.row.data.placeholder }}
          </div>
          <div v-else>
            <b-field class="ml-2">
              <b-input
                v-model="props.row.modify.placeholder"
                :placeholder="$t('attributes.placeholder')"
                @input="props.row.errors['placeholder'] = null"
              ></b-input>
            </b-field>
          </div>
        </b-table-column>

        <b-table-column
          field="traveler_types"
          :label="$t('attributes.traveler_type')"
          style="min-width: 180px;"
          v-slot="props"
        >
          <div v-if="!props.row.editing && props.row.id">
            <b-field
              v-for="type in props.row.modify.traveler_types"
              :key="type.name"
              :label="type.name"
              class="ml-2 my-4"
            >
              <b-input
                v-model="type.price_variation"
                type="number"
                disabled
              ></b-input>
            </b-field>
          </div>
          <div v-else>
            <b-field
              v-for="type in props.row.modify.traveler_types"
              :key="type.name"
              :label="type.name"
              label-position="on-border"
              class="ml-2 my-4"
            >
              <b-input v-model="type.price_variation" type="number"></b-input>
            </b-field>
          </div>
        </b-table-column>

        <b-table-column :width="80" v-slot="props">
          <div v-if="props.row.id === 0">
            <b-button type="is-warning" @click.prevent="onAdd(props.row)">
              {{ $t('attributes.add') }}
            </b-button>
          </div>
          <div v-else-if="!props.row.editing">
            <div class="buttons">
              <b-button type="is-primary" @click.prevent="editing(props.row)">
                {{ $t('attributes.modify') }}
              </b-button>
              <b-button type="is-danger" @click.prevent="onDelete(props.row)">
                {{ $t('attributes.delete') }}
              </b-button>
            </div>
          </div>
          <div v-else>
            <div class="buttons">
              <b-button type="is-primary" @click.prevent="doneEdit(props.row)">
                {{ $t('attributes.confirm') }}
              </b-button>
              <b-button
                type="is-success"
                outlined
                @click.prevent="cancelEdit(props.row)"
              >
                {{ $t('attributes.cancel') }}
              </b-button>
            </div>
          </div>
        </b-table-column>
      </b-table>
    </section>

    <footer class="modal-card-foot">
      <b-button class="is-fullwidth" @click="onClose">
        {{ $t('attributes.close') }}
      </b-button>
      <b-button
        :loading="sending"
        :disabled="editCount > 0"
        type="is-primary"
        class="is-fullwidth"
        @click.prevent="onSave"
      >
        {{ $t('attributes.save') }}
      </b-button>
    </footer>
  </div>
</template>

<script>
export default {
  name: 'CommonQuestionOptionsForm',

  props: {
    travelerTypes: {
      required: true,
      type: Array
    },
    question: {
      required: true,
      type: Object
    }
  },
  data() {
    const travelerNames = this.travelerTypes
      .map(t => t.traveler_type.name)
      .join(',')

    const items = this.question.options
      .sort((a, b) => a.sort - b.sort)
      .map((item, idx) => {
        const names = item.traveler_types
          ? item.traveler_types.map(t => t.name).join(',')
          : ''

        const traveler_types =
          names === travelerNames
            ? item.traveler_types
            : this.travelerTypes.map(t => ({
                name: t.traveler_type.name,
                price_variation: 0
              }))

        return {
          id: idx + 1,
          is_new: false,
          deleted: false,
          editing: false,
          errors: {},
          data: {
            name: item.name.trim(),
            sort: item.sort || 20,
            type: item.type,
            placeholder: item.placeholder.trim(),
            traveler_types: traveler_types
          },
          origin: {
            name: item.name.trim(),
            sort: item.sort || 20,
            type: item.type,
            placeholder: item.placeholder.trim(),
            traveler_types: traveler_types
          },
          modify: {
            name: item.name.trim(),
            sort: item.sort || 20,
            type: item.type,
            placeholder: item.placeholder.trim(),
            traveler_types: traveler_types
          }
        }
      })

    this.addNewItem(items)

    return {
      sending: false,
      modify: false,
      sortOptions: [...Array(20).keys()].map(i => i + 1),
      items,
      count: items.length,
      editCount: 0
    }
  },
  computed: {
    title() {
      return (
        this.$t('attributes.modify') +
        this.$t('attributes.common_question_option')
      )
    },
    filteredItems() {
      return this.items.filter(item => !item.deleted)
    }
  },
  watch: {
    question(newValue) {
      const travelerNames = this.travelerTypes
        .map(t => t.traveler_type.name)
        .join(',')

      const items = newValue.options
        .sort((a, b) => a.sort - b.sort)
        .map((item, idx) => {
          const names = item.traveler_types
            ? item.traveler_types.map(t => t.name).join(',')
            : ''

          const traveler_types =
            names === travelerNames
              ? item.traveler_types
              : this.travelerTypes.map(t => ({
                  name: t.traveler_type.name,
                  price_variation: 0
                }))

          return {
            id: idx + 1,
            is_new: false,
            deleted: false,
            editing: false,
            errors: {},
            data: {
              name: item.name.trim(),
              sort: item.sort || 20,
              type: item.type,
              placeholder: item.placeholder.trim(),
              traveler_types: traveler_types
            },
            origin: {
              name: item.name.trim(),
              sort: item.sort || 20,
              type: item.type,
              placeholder: item.placeholder.trim(),
              traveler_types: traveler_types
            },
            modify: {
              name: item.name.trim(),
              sort: item.sort || 20,
              type: item.type,
              placeholder: item.placeholder.trim(),
              traveler_types: traveler_types
            }
          }
        })

      this.addNewItem(items)

      this.sending = false
      this.modify = false
      this.items = items
      this.count = items.length
    }
  },
  methods: {
    addNewItem(items) {
      items.push({
        id: 0,
        is_new: true,
        deleted: false,
        editing: false,
        errors: {},
        data: {
          name: '',
          sort: 20,
          type: 'Option',
          placeholder: '',
          traveler_types: this.travelerTypes.map(t => ({
            name: t.traveler_type.name,
            price_variation: 0
          }))
        },
        origin: {
          name: '',
          sort: 20,
          type: 'Option',
          placeholder: '',
          traveler_types: this.travelerTypes.map(t => ({
            name: t.traveler_type.name,
            price_variation: 0
          }))
        },
        modify: {
          name: '',
          sort: 20,
          type: 'Option',
          placeholder: '',
          traveler_types: this.travelerTypes.map(t => ({
            name: t.traveler_type.name,
            price_variation: 0
          }))
        }
      })
    },
    isValid(item) {
      const attributes = {
        name: 50,
        type: 50
      }
      let valid = true

      const errors = {}
      Object.keys(attributes).forEach(att => {
        if (!item.modify[att]) {
          valid = false
          errors[att] = '欄位不可空白'
        } else if (item.modify[att].length > attributes[att]) {
          valid = false
          errors[att] = '長度不可超過' + attributes[att]
        } else {
          errors[att] = null
        }
      })

      const idx = this.items.findIndex(i => i.id === item.id)
      this.items[idx].errors = errors
      return valid
    },
    onAdd(item) {
      if (!this.isValid(item)) {
        return
      }

      item.id = this.count
      item.editing = false
      item.data = { ...item.modify }

      this.modify = true
      this.addNewItem(this.items)
      this.count = this.items.length
    },
    editing(item) {
      item.editing = true
      this.editCount += 1
    },
    doneEdit(item) {
      if (!this.isValid(item)) {
        return
      }

      if (
        item.data.name !== item.modify.name ||
        item.data.sort !== item.modify.sort ||
        item.data.type !== item.modify.type ||
        item.data.placeholder !== item.modify.placeholder ||
        JSON.stringify(item.data.traveler_types) !==
          JSON.stringify(item.modify.traveler_types)
      ) {
        this.modify = true
      }

      this.editCount -= 1
      item.editing = false
      item.data = {
        name: item.modify.name.trim(),
        sort: item.modify.sort,
        type: item.modify.type,
        placeholder: item.modify.placeholder,
        traveler_types: item.modify.traveler_types
      }
    },
    cancelEdit(item) {
      item.editing = false
      item.modify = { ...item.data }
      item.errors = {}
      this.editCount -= 1
    },
    onDelete(item) {
      this.$buefy.dialog.confirm({
        message: this.$t('attributes.confirm_delete'),
        onConfirm: () => {
          if (!item.is_new) {
            this.modify = true
            item.deleted = true
          } else {
            this.items = this.items.filter(i => i.id !== item.id)
          }
        }
      })
    },
    onSave() {
      if (this.editCount > 0) {
        return
      }

      const items = this.items
        .filter(i => i.id !== 0)
        .map(i => ({
          is_new: i.is_new,
          deleted: i.deleted,
          origin_name: i.origin.name,
          name: i.data.name,
          sort: i.data.sort,
          type: i.data.type,
          placeholder: i.data.placeholder,
          traveler_types: i.data.traveler_types
        }))

      this.$emit('send', items)
    },
    onClose() {
      if (this.editCount > 0) {
        this.$buefy.dialog.confirm({
          message: this.$t('message.confirm_close'),
          onConfirm: () => {
            this.$parent.close()
          }
        })
      } else {
        this.$parent.close()
      }
    },
    typeName(type) {
      let name = ''

      switch (type) {
        case 'Option':
          name = this.$t('attributes.option')
          break
        case 'Text':
          name = this.$t('attributes.text')
          break
        default:
          name = '類型錯誤'
          break
      }

      return name
    }
  }
}
</script>

<style lang="scss" scoped>
.modal-card {
  min-width: 960px;
  min-height: 540px;

  .table {
    td {
      vertical-align: middle;

      .control {
        margin: -0.5rem -0.75rem;
      }
    }
  }
}

@media (max-width: 960px) {
  .modal-card {
    min-width: 760px;
  }
}

@media (max-width: 768px) {
  .modal-card {
    min-width: auto;
  }
}
</style>
